import { Controller } from '@hotwired/stimulus'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

let libraryDeps
let Uppy, AwsS3, Informer, ThumbnailGenerator, DropTarget
let GlobalWorkerOptions, getDocument

export default class extends Controller {
  static targets = [
    // Dummy file input and hidden input field that's submitted with the form, respectively.
    // Typically autogenerated with simple_form_for:
    // = simple_form_for(@branding) do
    //   %div{ 'data-controller': 'file-upload' }
    //     = f.input(:logo, as: :attachment)
    //     ...
    'input',
    'result',
    // Image preview area
    'preview',
  ]

  static values = {
    type: { type: String, default: 'branding' }, // E.g., old-standard, left-align, etc.
    maxFileSize: { type: Number, default: 25 << 20 }, // allowed 25MB by default
  }

  static outlets = ['file-upload-ui', 'banner-image-upload']

  initialize() {
    // These libs are pretty hefty, so we only want to load them when there's an actual file-upload controller
    libraryDeps ??= Promise.all([
      import('uppy').then(
        (mod) =>
          ({ Uppy, AwsS3, Informer, ThumbnailGenerator, DropTarget } = mod),
      ),
      import('uppy/dist/uppy.css'),
    ])
    if (this.typeValue === 'post_documents') {
      libraryDeps = Promise.all([
        libraryDeps,
        import('pdfjs-dist').then(
          (mod) => ({ GlobalWorkerOptions, getDocument } = mod),
        ),
        // I was running into issues with the thumbnail of the first PDF being broken.
        // Explicitly importing the PDF worker seems to fix this.
        import(workerSrc),
      ])
    }
  }

  async connect() {
    // If the input target doesn't exist, the feature flag is off. Don't do anything
    if (!this.hasInputTarget) {
      return
    }

    await libraryDeps

    switch (this.typeValue) {
      case 'post_documents':
        // The workerSrc property shall be specified.
        // https://mozilla.github.io/pdf.js/examples/
        GlobalWorkerOptions.workerSrc = workerSrc
        break
      case 'old-standard':
      case 'modal':
        // Set default values for the old-standard type
        this.inputTarget.dataset.width ??= 100
        this.inputTarget.dataset.height ??= 100
        this.inputTarget.dataset.crop ??= 'fill'
        this.inputTarget.dataset.class ??= 'img-rounded'
    }

    this.createUppy()
    // for the image-size-validation controller
    if (this.hasPreviewTarget)
      this.inputTarget.dispatchEvent(new CustomEvent('initializeattachinary'), {
        detail: [this.previewTarget.querySelector('img')],
      })
  }

  disconnect() {
    this.uppy?.destroy()
  }

  createUppy() {
    const allowedFileTypes = this.inputTarget.accept.split(',')
    const uppy = new Uppy({
      id: this.inputTarget.id,
      restrictions: {
        allowedFileTypes,
        maxFileSize: this.maxFileSizeValue,
      },
    }).use(AwsS3, {
      endpoint: `${window.location.origin}/`, // will call Shrine's presign endpoint mounted on `/s3/params`
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
          .content,
      },
      allowedMetaFields: true,
    })

    // If we don't specify a width or a height, then we just want to display the raw image.
    // No need for the thumbnail generator
    // If we specify both, we also don't want to use the thumbnail generator,
    // because it will just resize according to the width and ignore the height.
    const needsThumbnail =
      !!this.inputTarget.dataset.width ^ !!this.inputTarget.dataset.height

    if (this.hasFileUploadUiOutlet) {
      this.#setupUiOutlet(uppy, needsThumbnail)
    } else {
      uppy.use(Informer, {
        target: (this.typeValue === 'post_documents'
          ? this.inputTarget.closest('.btn-file')
          : this.inputTarget
        ).parentNode,
      })
    }

    this.uppy = uppy

    if (needsThumbnail) {
      uppy.use(ThumbnailGenerator, {
        thumbnailWidth: this.inputTarget.dataset.width || null,
        thumbnailHeight: this.inputTarget.dataset.height || null,
        thumbnailType: 'image/png',
      })
    } else {
      // The ThumbnailGenerate plugin does the same thing to prevent memory leaks
      uppy.on('file-removed', (file) => {
        URL.revokeObjectURL(file.preview)
      })
    }

    this.submitButtons =
      this.inputTarget.form.querySelectorAll('input[type=submit]')
    this.inputTarget.addEventListener('change', (event) => {
      for (const file of event.target.files) {
        try {
          const fileId = uppy.addFile({
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file,
          })
          if (!needsThumbnail) {
            uppy.setFileState(fileId, {
              preview: URL.createObjectURL(file),
              needsPDFThumbnail: this.typeValue === 'post_documents',
            })
          }
        } catch (err) {
          if (err.isRestriction) {
            // handle restrictions
            console.log('Restriction error:', err)
            if (this.typeValue === 'file_list') this.#showUploadError(err.message)
          } else {
            // handle other errors
            console.error(err)
          }
        }
      }
      this.#redraw()
      this.inputTarget.value = null
      if (this.typeValue === 'post_documents') {
        this.#createPDFThumbnails()
      }
    })

    uppy.on('progress', (progress) => {
      const label = `[${progress}%] Uploading...`
      for (const e of this.submitButtons) {
        if (e.disabled) {
          e.value = label
        }
      }
    })

    uppy.on('upload-success', (file, response) => {
      const s3_key = decodeURIComponent(response.uploadURL).match(
        /\/cache\/([^?]+)/,
      )[1] // object key without prefix
      // set hidden field value to the uploaded file data so that it's submitted with the form as the attachment
      uppy.setFileState(file.id, {
        recordData: {
          file: {
            id: s3_key,
            storage: 'cache',
            metadata: {
              size: file.size,
              filename: file.name,
              mime_type: file.type,
            },
          },
        },
      })
      console.log('successful upload!')
    })

    // Clear the `<input>` after the upload or when the file was removed
    // so the file can be uploaded again (see
    // https://github.com/transloadit/uppy/issues/2640#issuecomment-731034781).
    uppy.on('error', this.#redraw.bind(this))
    uppy.on('complete', () => {
      this.#redraw()

      // for the ai-caption controller
      this.inputTarget.dispatchEvent(new CustomEvent('attachinary:fileadded'))
    })
    uppy.on('file-removed', this.#redraw.bind(this))

    // Add files to Uppy if the hidden field was pre-populated
    if (this.resultTarget.value) {
      for (const file of JSON.parse(this.resultTarget.value)) {
        const fileId = uppy.addFile({
          name: file.file.metadata.filename || file.file.id,
          type: allowedFileTypes?.[0] ?? file.file.metadata.mime_type,
          data: new Blob(),
        })
        uppy.setFileState(fileId, {
          progress: { uploadComplete: true, uploadStarted: true },
          preview: file.preview,
          needsPDFThumbnail: this.typeValue === 'post_documents',
          recordData: file,
        })
      }
      if (this.typeValue === 'post_documents') {
        // We need to generate the thumbnails before drawing the preview
        this.#createPDFThumbnails().then(() => {
          this.drawPreviews(uppy.getFiles())
        })
      } else {
        this.drawPreviews(uppy.getFiles())
      }
    }
    uppy.setOptions({ autoProceed: true })

    return uppy
  }

  #setupUiOutlet(uppy, needsThumbnail) {
    const target = this.fileUploadUiOutletElement
    // Disable the drop target UI when appropriate
    const callback = (e) => {
      if (this.inputTarget.disabled) e.stopImmediatePropagation()
    }
    target.addEventListener('dragover', callback)
    target.addEventListener('dragleave', callback)
    target.addEventListener('drop', callback)
    if (this.typeValue !== 'file_list') uppy.use(Informer, { target })
    uppy.on('restriction-failed', (_file, err) => {
      if (this.typeValue === 'file_list') this.#showUploadError(err.message)
    })
    uppy.use(DropTarget, {
      target,
      onDrop: () => {
        if (!needsThumbnail) {
          for (const file of uppy.getFiles()) {
            if (file.preview == null)
              uppy.setFileState(file.id, {
                preview: URL.createObjectURL(file.data),
                needsPDFThumbnail: this.typeValue === 'post_documents',
              })
          }
        }
        this.#redraw()
        if (this.typeValue === 'post_documents') {
          this.#createPDFThumbnails()
        }
      },
    })
    if (this.typeValue === 'file_list') {
      target.addEventListener('click', (e) => {
        if (!e.target.closest('[data-file-upload-ui-target="button"]')) {
          this.inputTarget.click()
        }
      })
    }
    this.fileUploadUiOutlet.buttonTarget.addEventListener('click', () => {
      const files = uppy.getFiles()
      if (this.inputTarget.multiple || files.length === 0) {
        this.inputTarget.click()
      } else {
        uppy.removeFile(files[0].id)
      }
    })
  }

  // Public wrapper so external callers (e.g. the admin-agent attach_file_to_field
  // Turbo Stream handler) can trigger PDF thumbnail generation without reaching
  // into the private #createPDFThumbnails method.
  generatePDFThumbnails() {
    return this.#createPDFThumbnails()
  }

  removeFile(event) {
    event.preventDefault()
    event.target.remove()
    this.uppy.removeFile(event.params.id)
    // for the image-size-validation controller
    this.inputTarget.dispatchEvent(new CustomEvent('removeimage'))
    // for the ai-caption controller
    this.inputTarget.dispatchEvent(new CustomEvent('attachinary:fileremoved'))
  }

  updateFileAttribute(e) {
    const file = this.uppy.getFile(e.params.id)
    if (e.target.id.startsWith('file-name')) {
      file.recordData.name = e.target.value
    } else {
      file.recordData.description = e.target.value
    }
    this.#setHiddenField(this.uppy.getFiles())
  }

  #redraw() {
    let files = this.uppy.getFiles()
    if (files.findIndex((f) => !f.progress.uploadComplete) > -1) {
      // Still uploading, disable inputs but don't do anything else yet
      this.inputTarget.disabled = true
      if (this.hasFileUploadUiOutlet)
        this.fileUploadUiOutlet.buttonTarget.disabled = true
      for (const e of this.submitButtons) {
        e.dataset.originalValue ??= e.value
        e.value = 'Uploading...'
        e.disabled = true
      }
      return
    }

    this.drawPreviews(files)

    this.inputTarget.value = null
    this.inputTarget.disabled = !this.inputTarget.multiple && files.length > 0
    if (this.hasFileUploadUiOutlet)
      this.fileUploadUiOutlet.buttonTarget.disabled = false
    for (const e of this.submitButtons) {
      e.value = e.dataset.originalValue ?? e.value
      e.disabled = false
    }

    this.#setHiddenField(files)
  }

  drawPreviews(files) {
    this.element.classList.toggle('file-upload-empty', files.length === 0)
    if (this.typeValue === 'post_image') {
      this.#drawPhotoPreview(files[0])
      return
    }
    if (this.typeValue === 'banner_image') {
      this.#drawBannerPreview(files[0])
      return
    }
    if (this.typeValue === 'file_list') {
      // No image thumbnails — render a simple staged-file row per file.
      this.previewTarget.innerHTML = ''
      for (const file of files) {
        this.previewTarget.insertAdjacentHTML(
          'beforeend',
          this.#generateFileListPreview(file),
        )
      }
      this.previewTarget.style.display =
        files.length > 0
          ? this.previewTarget.dataset.display || 'block'
          : 'none'
      return
    }

    // Only relevant when we data-width and data-height is set.
    // Currently only using this to mimic the old Attachinary input design in admin/posts2.js,
    // but I decided to make the functionality generic in case we ever need to do something similar in the future.
    let objectFit
    switch (this.inputTarget.dataset.crop) {
      case 'fill':
        objectFit = 'cover'
        break
      case 'fit':
        objectFit = 'contain'
        break
      default:
        objectFit = 'fill'
        break
    }

    if (this.hasFileUploadUiOutlet && !this.inputTarget.multiple) {
      if (files.length > 0)
        this.fileUploadUiOutlet.setPreview(
          this.#generateThumbnail(files[0], objectFit),
        )
      else this.fileUploadUiOutlet.removePreview()
      return
    }

    this.previewTarget.innerHTML = ''
    for (const file of files) {
      // Generate an <img> tag using the input element's data-format, data-crop,
      // data-width, data-height, and data-class attributes to format/style the image
      const thumbnail = this.#generateThumbnail(file, objectFit)
      this.previewTarget.insertAdjacentHTML(
        'beforeend',
        this.#generatePreview(
          file.id,
          thumbnail,
          file.recordData.name,
          file.recordData.description,
        ),
      )
    }
    this.previewTarget.style.display =
      files.length > 0 ||
      this.typeValue === 'old-standard' ||
      this.typeValue === 'modal'
        ? this.previewTarget.dataset.display || 'block'
        : 'none'
  }

  #setHiddenField(files) {
    // If the hidden field was previously disabled and there are no files,
    // then the user has just removed the default image.
    // Thus the hidden filed should be enabled
    if (files.length === 0) this.resultTarget.disabled = false
    this.resultTarget.value =
      files.length > 0 ? JSON.stringify(files.map((f) => f.recordData)) : ''
  }

  #generateThumbnail(file, objectFit) {
    const dataset = this.inputTarget.dataset
    return `<img
        src="${file.preview.replaceAll('&', '&amp;')}"
        width="${dataset.width || ''}"
        height="${dataset.height || ''}"
        class="${dataset.class || ''}"
        style="object-fit: ${objectFit}; ${dataset.style || ''}"
      >`
  }

  /**
   * Returns HTML representing a preview of a single image.
   * @param {string} fileId - The public ID of the uploaded file.
   * @param {string} imageHtml - The HTML for the <img> tag.
   * @return A string containing HTML.
   */
  #generatePreview(fileId, imageHtml, name, description) {
    // Customizeable preview HTML
    switch (this.typeValue) {
      case 'old-standard':
        document
          .querySelector(`[data-file-upload-id-param="${fileId}"]`)
          ?.remove()
        this.inputTarget.parentNode.insertAdjacentHTML(
          'beforeend',
          this.#removeButton(fileId, { class: 'pull-right' }),
        )
        return `
        <div
          id='file-preview-${fileId}' style='text-align: right;'
        >
          ${imageHtml}
        </div>
      `
      case 'modal':
        document
          .querySelector(`[data-file-upload-id-param="${fileId}"]`)
          ?.remove()
        this.inputTarget.parentNode.insertAdjacentHTML(
          'beforeend',
          this.#removeButton(fileId),
        )
        return `
        <div
          id='file-preview-${fileId}'
        >
          ${imageHtml}
        </div>
      `
      case 'left-align':
        return `
        <div
          id='file-preview-${fileId}'
        >
          ${imageHtml}<br>
          ${this.#removeButton(fileId)}
        </div>
      `
      case 'scrollable':
        return `
        <div
          id='file-preview-${fileId}' style='text-align: right;'
        >
          <div style='display: inline-block; width: 100%; overflow-x: auto;'>
            ${imageHtml}
          </div>
          ${this.#removeButton(fileId)}
        </div>
      `

      case 'unformatted':
        return `
          ${imageHtml}
          ${this.#removeButton(fileId, { class: 'btn btn-link' })}
        `

      case 'branding':
      default:
        return `
        <div
          id='file-preview-${fileId}'
        >
          ${imageHtml}
          ${this.#removeButton(fileId, { class: 'pull-right' })}
        </div>
      `

      case 'post_documents':
        return `
        <tr id="file-preview-${fileId}">
          <td style="width: 170px;">
            ${imageHtml}
          </td>
          <td>
            <div class="form-group mb-3">
              <label class="col-sm-2 control-label form-label" for="file-name-${fileId}">
                File Name
              </label>
              <div class="col-sm-10">
                <input
                  class="string optional text_field form-control"
                  type="text"
                  name="post[documents_attributes][${fileId}][name]"
                  value="${this.#escapeHtml(name || '')}"
                  id="file-name-${fileId}"
                  data-action="change->file-upload#updateFileAttribute"
                  data-file-upload-id-param="${fileId}"
                >
              </div>
            </div>


            <div class="form-group">
              <label class="col-sm-2 control-label form-label" for="file-description-${fileId}">
                Description
              </label>
              <div class="col-sm-10">
                <textarea
                  class="optional text_field form-control"
                  name="post[documents_attributes][${fileId}][description]"
                  id="file-description-${fileId}"
                  data-action="change->file-upload#updateFileAttribute"
                  data-file-upload-id-param="${fileId}"
                >${this.#escapeHtml(description || '')}</textarea>
                </div>
            </div>
          </td>
          <td style="width: 40px; text-align: right;">
            ${this.#removeButton(fileId, { text: '<i class="fas fa-times" style="color: red;"></i>' })}
          </td>
        </tr>
      `
    }
  }

  #showUploadError(message) {
    let errorEl = this.element.querySelector('.file-upload-error')
    if (!errorEl) {
      errorEl = document.createElement('p')
      errorEl.className = 'file-upload-error text-danger small'
      this.previewTarget.before(errorEl)
    }
    errorEl.textContent = message
    clearTimeout(this._errorTimeout)
    this._errorTimeout = setTimeout(() => errorEl.remove(), 5000)
  }

  #generateFileListPreview(file) {
    const name = this.#escapeHtml(file.name || '')
    const size = file.size ? this.#formatBytes(file.size) : ''
    const removeBtn = this.#removeButton(file.id, {
      class: 'staged-file__remove',
      text: '<i class="far fa-times" aria-hidden="true"></i>',
    })
    return `
      <div id="file-preview-${file.id}" class="staged-file">
        <span class="staged-file__icon far fa-file" aria-hidden="true"></span>
        <span class="staged-file__name">${name}${size ? ` <span class="text-muted">(${size})</span>` : ''}</span>
        ${removeBtn}
      </div>
    `
  }

  #escapeHtml(str) {
    return String(str).replace(
      /[&<>"']/g,
      (c) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[c],
    )
  }

  #formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  #removeButton(fileId, options = {}) {
    return `
      <a
        href="#"
        role="button"
        data-action="file-upload#removeFile"
        data-file-upload-id-param="${fileId}"
        class="${options.class || ''}"
        style="${options.style || ''}"
      >
        ${options.text || 'Remove Image'}
      </a>
    `
  }

  // Draw banner image preview using Stimulus outlet (no jQuery/selectors)
  #drawBannerPreview(file) {
    if (this.hasBannerImageUploadOutlet) {
      if (file == null) {
        this.bannerImageUploadOutlet.showEmptyState()
      } else {
        this.bannerImageUploadOutlet.showPreview(file)
      }
    }
  }

  // TODO: Refactor without jQuery
  #drawPhotoPreview(file) {
    if (file == null) {
      // Reset UI to have no image preview
      $(`#photo-preview`).html('')
      $('#photo-preview').html('').css('display', 'none')
      $('#post-photo-label').show()
      $("[name='post[photo]']").prop('disabled', false)
      $('#photo-remove').html('')
      $('#photo-remove').css('display', 'none')
      return
    }

    // Only show Remove Image link if image is not linked
    const removePreviewLink = file.recordData.is_linked
      ? ''
      : this.#removeButton(file.id)

    const previewHtml = `<div
    id='file-preview-${file.id}'
    style='display: flex; flex-direction: column;'
  >
    <img src="${file.preview}" width="100">
  </div>`

    // For improved UX, add preview to custom div
    // TODO: Rewrite with vanilla JS
    $('#photo-preview').append(previewHtml).css('display', 'flex')
    $('#photo-remove').html(removePreviewLink)
    $('#photo-remove').css('display', 'flex')
    $('#post-photo-label').hide()
    $("[name='post[photo]']").prop('disabled', true)
  }

  // Generate thumbnails for PDF files using PDFjs
  #createPDFThumbnails() {
    return Promise.allSettled(
      this.uppy
        .getFiles()
        .filter((file) => file.needsPDFThumbnail)
        .map(async (file) => {
          // See https://github.com/scandel/pdfThumbnails/blob/64d720e5daef3044fe30b297e1e4abab6f620368/pdfThumbnails.js#L52
          const pdf = await getDocument({ url: file.preview }).promise
          URL.revokeObjectURL(file.preview)
          const page = await pdf.getPage(1)

          let canvas = document.createElement('canvas')
          let viewport = page.getViewport({ scale: 1.0 })
          let context = canvas.getContext('2d')

          viewport = page.getViewport({ scale: 150 / viewport.width })

          canvas.height = viewport.height
          canvas.width = viewport.width

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise

          const blob = await new Promise((res) => canvas.toBlob(res))
          this.uppy.setFileState(file.id, {
            preview: URL.createObjectURL(blob),
            needsPDFThumbnail: false,
          })
        }),
    )
  }
}
